import type { Customer } from '../types/customer'
import type { AppKind, Deployment } from '../types/deployment'

/** The unit everything trades in: a deployment paired with its customer. */
export interface TenantEntry<TSettings = Record<string, unknown>> {
	customer: Customer
	deployment: Deployment<TSettings>
}

export interface TenantRegistry<TSettings = Record<string, unknown>> {
	entries: TenantEntry<TSettings>[]
	byKey: Record<string, TenantEntry<TSettings>>
	byHost(host: string): TenantEntry<TSettings> | undefined
	keys(): string[]
	customers(): Customer[]
}

const KEY_PATTERN = /^[a-z0-9-]+\/(booking|portal)\/[a-z0-9-]+$/

export function assertValidKey(key: string): void {
	if (!KEY_PATTERN.test(key)) {
		throw new Error(
			`[tenant-registry] Invalid deployment key "${key}". ` +
				`Expected <customerId>/<app>/<releaseLevel>[-qualifier], lowercase slugs.`,
		)
	}
}

/** Deployment literals omit `customerId` and `app` (both stamped); stating either is allowed but must match. */
export type DeploymentInput<TSettings = Record<string, unknown>> = Omit<
	Deployment<TSettings>,
	'customerId' | 'app'
> & {
	customerId?: string
	app?: AppKind
}

function pairDeployments<TSettings>(
	customer: Customer,
	app: AppKind,
	deployments: DeploymentInput<TSettings>[],
): TenantEntry<TSettings>[] {
	const seen = new Set<string>()
	const environments = Object.keys(customer.environments ?? {})

	return deployments.map((input) => {
		if (input.customerId !== undefined && input.customerId !== customer.id) {
			throw new Error(
				`[tenant-registry] Deployment "${input.key}" declares customerId ` +
					`"${input.customerId}" but belongs to customer "${customer.id}".`,
			)
		}
		if (input.app !== undefined && input.app !== app) {
			throw new Error(
				`[tenant-registry] Deployment "${input.key}" declares app "${input.app}" ` +
					`in a ${app} definition.`,
			)
		}
		assertValidKey(input.key)
		if (!input.key.startsWith(`${customer.id}/`)) {
			throw new Error(
				`[tenant-registry] Deployment key "${input.key}" must start with "${customer.id}/".`,
			)
		}
		if (input.key.split('/')[1] !== app) {
			throw new Error(
				`[tenant-registry] Deployment key "${input.key}" does not name the "${app}" app.`,
			)
		}
		if (seen.has(input.key)) {
			throw new Error(`[tenant-registry] Duplicate deployment key "${input.key}".`)
		}
		seen.add(input.key)

		// The cross-check that makes hoisting endpoints onto the customer safe: a
		// deployment naming an environment that does not exist would otherwise ship
		// with no api origin at all.
		if (!environments.includes(input.environment)) {
			throw new Error(
				`[tenant-registry] Deployment "${input.key}" names environment ` +
					`"${input.environment}", which "${customer.id}" does not define. ` +
					`Available: ${environments.join(', ') || '(none)'}.`,
			)
		}

		if (app === 'portal' && !input.portalType) {
			throw new Error(`[tenant-registry] Portal deployment "${input.key}" needs a portalType.`)
		}
		if (app === 'booking' && input.portalType) {
			throw new Error(
				`[tenant-registry] Booking deployment "${input.key}" must not set portalType.`,
			)
		}

		const deployment = { ...input, app, customerId: customer.id } as Deployment<TSettings>
		return { customer, deployment }
	})
}

export function defineBookingDeployments<TSettings = Record<string, unknown>>(
	customer: Customer,
	deployments: DeploymentInput<TSettings>[],
): TenantEntry<TSettings>[] {
	return pairDeployments(customer, 'booking', deployments)
}

export function definePortalDeployments<TSettings = Record<string, unknown>>(
	customer: Customer,
	deployments: DeploymentInput<TSettings>[],
): TenantEntry<TSettings>[] {
	return pairDeployments(customer, 'portal', deployments)
}

export function defineTenantRegistry(entries: TenantEntry[]): TenantRegistry {
	const byKey: Record<string, TenantEntry> = {}
	for (const entry of entries) {
		const key = entry.deployment.key
		if (byKey[key]) {
			throw new Error(`[tenant-registry] Duplicate deployment key "${key}" in registry.`)
		}
		byKey[key] = entry
	}

	return {
		entries,
		byKey,
		// First-declared wins on shared hosts; key lookup is the only disambiguator.
		byHost: (host: string) => entries.find((e) => e.deployment.host === host),
		keys: () => entries.map((e) => e.deployment.key),
		customers: () => {
			const seen = new Map<string, Customer>()
			for (const e of entries) {
				if (!seen.has(e.customer.id)) seen.set(e.customer.id, e.customer)
			}
			return [...seen.values()]
		},
	}
}

/** The environment a deployment points at. Throws if the pairing is inconsistent. */
export function environmentFor(entry: TenantEntry) {
	const env = entry.customer.environments?.[entry.deployment.environment]
	if (!env) {
		throw new Error(
			`[tenant-registry] ${entry.deployment.key}: customer "${entry.customer.id}" ` +
				`defines no environment "${entry.deployment.environment}".`,
		)
	}
	return env
}

/** Convenience for the many consumers that only ever want the api origin. */
export function apiOriginFor(entry: TenantEntry): string {
	return environmentFor(entry).endpoints.api
}
